import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Layout,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  message
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import organizationService from '../services/organizationService';
import { canCreateOrganizations, canViewOrganizations, isSuperAdmin } from '../utils/permissions';

function Organizations() {
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);

  useEffect(() => {
    const loadOrganizations = async () => {
      if (!canViewOrganizations(user)) {
        return;
      }

      setLoading(true);
      try {
        if (isSuperAdmin(user)) {
          const response = await organizationService.getAll();
          setOrganizations(response.data || []);
        } else if (user?.organizationId) {
          const response = await organizationService.getById(user.organizationId);
          setCurrentOrganization(response.data || null);
        }
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, [user]);

  const handleCreateOrganization = async (values) => {
    setLoading(true);
    try {
      const response = await organizationService.create(values);
      setOrganizations((prev) => [response.data, ...prev]);
      message.success('Organization created successfully');
      form.resetFields();
      setIsModalOpen(false);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value) => value || '-'
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (value) => value || '-'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Active' : 'Inactive'}</Tag>
    }
  ];

  return (
    <Layout style={{ padding: '20px' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>Organizations</h1>
            <p style={{ margin: 0, color: '#667085' }}>
              SuperAdmin can create organizations. Admin and Manager can view their own organization details.
            </p>
          </div>
          {canCreateOrganizations(user) && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
              Create Organization
            </Button>
          )}
        </div>

        {!canViewOrganizations(user) && (
          <Alert type="warning" message="This role does not have access to organizations." />
        )}

        <Spin spinning={loading}>
          {isSuperAdmin(user) ? (
            <Card title="All Organizations">
              <Table columns={columns} dataSource={organizations} rowKey="id" pagination={{ pageSize: 10 }} />
            </Card>
          ) : (
            <Card title="My Organization">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Name">{currentOrganization?.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Email">{currentOrganization?.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{currentOrganization?.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="Website">{currentOrganization?.website || '-'}</Descriptions.Item>
                <Descriptions.Item label="Address">{currentOrganization?.address || '-'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={currentOrganization?.isActive ? 'green' : 'red'}>
                    {currentOrganization?.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Spin>
      </Space>

      <Modal
        title="Create Organization"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrganization}>
          <Form.Item label="Organization Name" name="name" rules={[{ required: true, message: 'Please enter organization name' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email">
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Website" name="website">
            <Input />
          </Form.Item>
          <Form.Item label="Address" name="address">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="City" name="city">
            <Input />
          </Form.Item>
          <Form.Item label="State" name="state">
            <Input />
          </Form.Item>
          <Form.Item label="Country" name="country">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Organizations;
